import React, { useMemo } from "react";
import { Boxes, Info, Layers, Pause, Percent, Play, Tag, Users } from "lucide-react";
import {
  Badge,
  Btn,
  Callout,
  Card,
  CardBody,
  CardHeader,
  KPI,
  KV,
} from "../../loyalty/components/primitives";

const TYPE_META = {
  free_slab:    { label: "Free Slab",         tone: "gold",   Icon: Layers },
  disc_material:{ label: "Material Discount", tone: "blue",   Icon: Tag },
  disc_bill_pct:{ label: "Bill %",            tone: "green",  Icon: Percent },
  disc_bill_amt:{ label: "Bill Flat ₹",       tone: "teal",   Icon: Percent },
  combo:        { label: "Combo",             tone: "purple", Icon: Boxes },
  employee:     { label: "Employee",          tone: "amber",  Icon: Users },
};

const STATUS_TONE = {
  active: "green",
  paused: "amber",
  scheduled: "blue",
  inactive: "slate",
  draft: "slate",
  archived: "slate",
};

const OverviewTab = ({ schemes = [], loading, goTo }) => {
  const counts = useMemo(() => {
    const by_type = {};
    let active = 0;
    let paused = 0;
    let scheduled = 0;
    for (const s of schemes) {
      if (s.status === "archived") continue;
      by_type[s.type] = (by_type[s.type] || 0) + 1;
      if (s.status === "active") active += 1;
      else if (s.status === "paused") paused += 1;
      else if (s.status === "scheduled") scheduled += 1;
    }
    return { by_type, active, paused, scheduled, total: schemes.length };
  }, [schemes]);

  return (
    <div className="space-y-5">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h2 className="text-[17px] font-bold text-[#0F172A]">Schemes Overview</h2>
          <p className="text-[13px] text-[#64748B] mt-1 max-w-[700px]">
            Bill-level promotions that the engine applies automatically at checkout.
            Six types — free slabs, material/bill discounts, combos and employee
            benefits — share one row shape with priority-based resolution.
          </p>
        </div>
        <div className="flex gap-2">
          <Btn variant="ghost" onClick={() => goTo("list")}>
            <Layers size={15} />
            All schemes
          </Btn>
          <Btn variant="primary" onClick={() => goTo("create")}>
            <Tag size={15} />
            New scheme
          </Btn>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <KPI
          icon={<Play size={18} />}
          tone="green"
          label="Currently active"
          value={loading ? "…" : counts.active}
          delta={`of ${counts.total} total`}
        />
        <KPI
          icon={<Pause size={18} />}
          tone="amber"
          label="Paused"
          value={loading ? "…" : counts.paused}
          delta="Admin-paused — not running"
        />
        <KPI
          icon={<Info size={18} />}
          tone="blue"
          label="Scheduled"
          value={loading ? "…" : counts.scheduled}
          delta="Will go live in window"
        />
        <KPI
          icon={<Layers size={18} />}
          tone="purple"
          label="Distinct types in use"
          value={loading ? "…" : Object.keys(counts.by_type).length}
          delta="Out of 6 supported"
        />
      </div>

      <Card>
        <CardHeader
          icon={<Layers size={18} />}
          tone="blue"
          title="By type"
          desc="Non-archived schemes grouped by their type-config shape"
        />
        <CardBody tight>
          {Object.keys(TYPE_META).map((t) => {
            const meta = TYPE_META[t];
            const Icon = meta.Icon;
            const n = counts.by_type[t] || 0;
            return (
              <KV
                key={t}
                label={
                  <span className="flex items-center gap-2">
                    <span
                      className="w-7 h-7 rounded-md flex items-center justify-center"
                      style={{
                        background: `var(--token-${meta.tone}-bg, #F1F5F9)`,
                      }}
                    >
                      <Icon size={14} />
                    </span>
                    <span className="font-semibold text-[#0F172A]">{meta.label}</span>
                  </span>
                }
                value={
                  <span className="flex items-center gap-2">
                    <span className="tabular-nums font-semibold">{n}</span>
                    <Badge tone={n > 0 ? meta.tone : "slate"}>
                      {n > 0 ? "in use" : "none"}
                    </Badge>
                  </span>
                }
              />
            );
          })}
        </CardBody>
      </Card>

      <Card>
        <CardHeader
          icon={<Play size={18} />}
          tone="green"
          title="Status breakdown"
          desc="State machine totals across the catalogue"
        />
        <CardBody tight>
          {["active", "scheduled", "paused", "inactive", "draft", "archived"].map((st) => {
            const n = schemes.filter((s) => s.status === st).length;
            return (
              <KV
                key={st}
                label={<Badge tone={STATUS_TONE[st]}>{st}</Badge>}
                value={<span className="tabular-nums font-semibold">{n}</span>}
              />
            );
          })}
        </CardBody>
      </Card>

      <Callout tone="blue" icon={<Info size={16} />}>
        Priority is <b>higher = applied first</b>. Ties break by oldest
        <code className="font-mono text-[12px] mx-1">created_at</code> first. Drag rows
        in the <b>Schemes</b> tab to re-rank — the engine reads the new order on the
        next bill.
      </Callout>
    </div>
  );
};

export default OverviewTab;
